<template>
  <panel class="mt-6 pt-6" v-if="loading">
    <loader />
  </panel>

  <panel class="mt-6 pt-6" v-else-if="!links">
    <div class="flex justify-center items-center my-12">
      <h2 class="text-grey-light text-4xl font-normal">No Engage Instance Found</h2>
    </div>
  </panel>

  <div v-else class="flex leading-normal">
    <div class="w-full md:w-1/2 px-2">
      <panel class="mt-6 pt-6">
        <h2 class="text-base mb-1">Standalone Instance</h2>
        <p class="mb-3">Link directly to your standalone Engage instance to capture leads.</p>
        <input
          type="text"
          class="w-full input rounded mb-6"
          readonly
          :value="links.direct_link"
        />
      </panel>

      <panel class="mt-6 pt-6">
        <h2 class="text-base mb-1">Embed</h2>
        <p class="mb-3">Embed your Engage instance into your website directly, or with a call to action.</p>
        <div class="bg-grey-lighter text-grey h-64 overflow-hidden rounded relative border mb-6">
          <p class="p-3">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis pharetra augue sed nisl ullamcorper, vel fermentum elit posuere</p>
          <div v-if="prompt.type !== 'window'" :class="{'bg-black-75 absolute pin': prompt.type === 'modal'}">
            <div class="rounded-lg overflow-hidden bg-white flex m-3">
              <div class="w-1/3 p-3" :style="{backgroundColor: bgColour}" v-if="prompt.branding">
                <img :src="logo" class="max-w-full" />
              </div>
              <div class="flex-grow p-3">
                <div class="bg-grey-light h-6 w-1/2 mb-1 rounded"></div>
                <div class="border h-8 w-full mb-3 rounded"></div>
                <div class="bg-grey-light h-6 w-1/2 mb-1 rounded"></div>
                <div class="border h-8 w-full mb-2 rounded"></div>
                <div class="h-8 w-1/4 rounded" :style="{backgroundColor: bgColour}"></div>
              </div>
            </div>
          </div>
          <img v-if="prompt.type !== 'inline'" :src="imageSrc" :class="prompt.attachment" :style="prompt.position" style="width: 300px;" />
          <p class="p-3">Donec ut lorem id ligula efficitur luctus at a sapien. Nam at risus ipsum. Nulla quis molestie lorem, in ultricies arcu. Nam tempus pretium ante sed faucibus. Phasellus fringilla felis lectus. Vivamus nisl ligula, dapibus vel rhoncus quis, dignissim nec tellus. Aliquam faucibus sapien ipsum, at vehicula felis placerat viverra. Quisque libero nisi, aliquam ac dictum ut, ullamcorper in nulla. Fusce luctus dui sit amet massa rutrum, id dignissim magna aliquam. Vestibulum volutpat posuere sapien, vitae suscipit justo fringilla pulvinar. Vestibulum malesuada magna at dapibus ornare. Phasellus porta elementum convallis. Vestibulum consequat feugiat elit nec varius. Maecenas interdum posuere tincidunt. Mauris ex quam, pulvinar vel turpis sed, suscipit elementum ex.</p>
        </div>

        <div class="rounded border p-6">
          <div class="flex mb-3">
            <div class="w-full flex flex-col mr-4">
              <label class="mb-1">Embed Type</label>
              <pl-select select-class="rounded" v-model="prompt.type">
                <option v-for="type in prompt.types" :value="type.id" :key="type.id">{{ type.name }}</option>
              </pl-select>
            </div>
            <div class="w-full flex flex-col">
              <label class="mb-1">Branding</label>
              <pl-select select-class="rounded" v-model="prompt.branding" :disabled="prompt.type === 'window'">
                <option v-for="branding in prompt.brandings" :value="branding.id" :key="branding.id">{{ branding.name }}</option>
              </pl-select>
            </div>
          </div>

          <div class="flex mb-3">
            <div class="w-full flex flex-col mr-4">
              <label class="mb-1">Size</label>
              <pl-select select-class="rounded" v-model="prompt.size">
                <option v-for="size in prompt.sizes" :value="size.id" :key="size.id">{{ size.name }}</option>
              </pl-select>
            </div>
            <div class="w-full flex flex-col mr-4">
              <label class="mb-1">Attachment</label>
              <pl-select select-class="rounded" v-model="prompt.attachment">
                <option v-for="attachment in prompt.attachments" :value="attachment.id" :key="attachment.id">{{ attachment.name }}</option>
              </pl-select>
            </div>
            <div class="w-full flex flex-col">
              <label class="mb-1">Position</label>
              <pl-select select-class="rounded" v-model="prompt.position" :disabled="prompt.attachment === 'static'">
                <option v-for="position in prompt.positions" :value="position.id" :key="position.id">{{ position.name }}</option>
              </pl-select>
            </div>
          </div>

          <div class="flex mb-3">
            <div class="w-1/2 flex flex-col mr-4">
              <label class="mb-1">Colour 1</label>
              <colour-picker-input v-model="prompt.colour1" />
            </div>
            <div class="w-1/2 flex flex-col">
              <label class="mb-1">Colour 2</label>
              <colour-picker-input v-model="prompt.colour2" />
            </div>
          </div>

          <div class="flex mb-3">
            <div class="w-1/2 flex flex-col mr-4">
              <label class="mb-1">Bubble Background Colour</label>
              <colour-picker-input v-model="prompt.bubbleBg" />
            </div>
            <div class="w-1/2 flex flex-col">
              <label class="mb-1">Bubble Foreground Colour</label>
              <colour-picker-input v-model="prompt.bubbleFg" />
            </div>
          </div>
        </div>

        <label class="mb-1 mt-6 block">Embed Code</label>
        <code class="w-full bg-grey-lighter border p-3 rounded overflow-auto whitespace-no-wrap block">
          <span v-if="prompt.type === 'window'">
            {{ externalLinkWithCta }}
          </span>
          <span v-else>
            &lt;!-- Place this after the opening body tag --&gt;<br />
            &lt;script src="https://unpkg.com/vue@2.6.10/dist/vue.min.js"&gt;&lt;/script&gt;<br />
            &lt;script src="https://{{ this.alias }}.engage.epropservices.com/eprop-engage.min.js"&gt;&lt;/script&gt;<br /><span v-if="!(prompt.type === 'modal' && prompt.attachment === 'absolute')"><br />
            &lt;!-- Place this where {{ prompt.type === 'modal' ? 'the call to action' : 'the Engage form' }} should appear --&gt;<br /></span>
            &lt;eprop-engage alias="{{ this.alias }}"<span v-if="prompt.branding === false"> headless="true"</span>&gt;{{ prompt.type === 'inline' ? '' : ctaImgTag }}&lt;/eprop-engage&gt;
          </span>
        </code>
      </panel>
    </div>

    <div class="w-full md:w-1/2 px-2">
      <panel class="mt-6 pt-6">
        <h2 class="text-base mb-1">Google Analytics</h2>
        <p class="mb-6">Track events from your Engage form with Google Analytics or Google Tag Manager. Simply embed Google Analytics to your website separately and we will start sending events using that tracking code. To track interactions from your standalone instance of Engage, please contact us with your Google Analytics tracking code.</p>

        <div class="border p-3 mb-6 rounded">
          <h2 class="text-base mb-1">Form Opened</h2>
          <p class="mb-6">Track when your visitors launch the Engage form.</p>
          <p><strong>Category equals: </strong> Engage</p>
          <p><strong>Action equals: </strong> initialised</p>
        </div>

        <div class="border p-3 rounded">
          <h2 class="text-base mb-1">Lead Captured</h2>
          <p class="mb-6">Track when your visitors have submitted the Engage form.</p>
          <p><strong>Category equals: </strong> Engage</p>
          <p><strong>Action equals: </strong> pageChange</p>
          <p><strong>Label equals: </strong> thank-you</p>
        </div>
      </panel>
    </div>
  </div>
</template>

<script>
import api from '@/http'
import { mapState } from 'vuex'
import ColourPickerInput from '@/components/ColourPickerInput'

export default {
  name: 'Links',

  components: {
    ColourPickerInput
  },

  data () {
    return {
      loading: true,
      links: {},
      alias: '',
      bgColour: '#000',
      logo: null,
      prompt: {
        colour1: '',
        colour2: '',
        bubbleBg: '',
        bubbleFg: '',
        size: 'small',
        sizes: [{
          id: 'large',
          name: 'Large'
        }, {
          id: 'small',
          name: 'Small'
        }],
        attachment: 'absolute',
        attachments: [{
          id: 'absolute',
          name: 'Fixed'
        }, {
          id: 'static',
          name: 'Inline'
        }],
        position: 'right: 20px; bottom: 10px;',
        positions: [{
          id: 'left: 20px; top: 10px;',
          name: 'Top Left'
        }, {
          id: 'right: 20px; top: 10px;',
          name: 'Top Right'
        }, {
          id: 'left: 20px; bottom: 10px;',
          name: 'Bottom Left'
        }, {
          id: 'right: 20px; bottom: 10px;',
          name: 'Bottom Right'
        }],
        type: 'modal',
        types: [
          {
            id: 'inline',
            name: 'Inline Form'
          },
          {
            id: 'modal',
            name: 'Modal with CTA'
          },
          {
            id: 'window',
            name: 'New Window with CTA'
          }
        ],
        branding: true,
        brandings: [
          {
            id: true,
            name: 'Visible'
          },
          {
            id: false,
            name: 'Hidden'
          }
        ]
      }
    }
  },

  computed: {
    ...mapState(['selectedOffice']),
    externalLink () {
      return `<a href="https://${this.alias}.engage.epropservices.com" target="_blank">Value My Home</a>`
    },
    externalLinkWithCta () {
      return `<a href="https://${this.alias}.engage.epropservices.com" target="_blank">${this.ctaImgTag}</a>`
    },
    externalPopUp () {
      return `<a href="#" onclick="javascript: window.open('https://${this.alias}.engage.epropservices.com', '_blank', 'toolbar=no,scrollbars=yes,resizable=yes,width=1200,height=750')">Value My Home</a>`
    },
    externalIframe () {
      return `<iframe src="https://${this.alias}.engage.epropservices.com" frameborder="0" style="width: 100%; height: 1500px"></iframe>`
    },
    externalScript () {
      return `<script async src="https://${this.alias}.engage.epropservices.com/engage/js/external.js"></scrip` + 't>'
    },
    imageSrc () {
      const size = this.prompt.size
      const colour1 = this.prompt.colour1.replace('#', '')
      const colour2 = this.prompt.colour2.replace('#', '')
      const bubbleBg = this.prompt.bubbleBg.replace('#', '')
      const bubbleFg = this.prompt.bubbleFg.replace('#', '')
      const networkId = this.selectedOffice.network_id
      return `https://${this.alias}.engage.epropservices.com/pages/engage/external-prompt.svg?size=${size}&colour1=${colour1}&colour2=${colour2}&bbg=${bubbleBg}&bcolour=${bubbleFg}&network=${networkId}`
    },
    embedCode () {
      const style = this.prompt.attachment === 'absolute' ? ` position: fixed; ${this.prompt.position}` : ''
      return `<img src="${this.imageSrc}" data-pl-engage="https://${this.alias}.engage.epropservices.com" alt="Book a Property Valuation" style="cursor: pointer; z-index: 900; width: 300px;${style}" />`
    },
    ctaImgTag () {
      const style = this.prompt.attachment === 'absolute' ? ` position: fixed; ${this.prompt.position}` : ''
      return `<img src="${this.imageSrc}" alt="Book a Property Valuation" style="cursor: pointer; z-index: 900; width: 300px;${style}" />`
    }
  },

  methods: {
    fetchLinks () {
      if (!this.selectedOffice.id) {
        return
      }

      this.loading = true
      api
        .get(`office/${this.selectedOffice.id}/engage/links`)
        .then(({ data }) => {
          this.alias = data.instance.alias
          this.prompt.colour1 = data.instance.colour1
          this.prompt.colour2 = data.instance.colour2
          this.prompt.bubbleBg = data.instance.colour1
          this.prompt.bubbleFg = data.instance.colour2
          this.bgColour = data.instance.colour1
          this.logo = data.instance.logo
          this.links = data.links
        })
        .catch(() => {
          this.links = {}
        }).finally(() => {
          this.loading = false
        })
    }
  },

  created () {
    this.fetchLinks()
  },

  watch: {
    selectedOffice () {
      this.fetchLinks()
    }
  }
}
</script>

<style lang="scss" scoped>
.bg-black-75 {
  background-color: rgba(0,0,0,.75);
}
</style>
